import React, { useState, useEffect } from "react";
import { get, post } from "../../../services/api";
import { toast } from "react-toastify";
import { useForm, useFieldArray } from "react-hook-form";
import { createPortal } from "react-dom";
import DropdownWithCustomValuesModal from "../../../components/DropdownWithCustomValuesModal";

const PersonalInfoForm = ({ selectedStudent, setProfilePicture }) => {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const centerId = localStorage.getItem("centerId");
  const userId = selectedStudent.userId;
  const code = selectedStudent.familyCode;
  const [profilePictureState, setProfilePictureState] = useState(null);

  const {
    register,
    watch,
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      admissionInquiryDtos: [],
      currentPupil: {
        id: 0,
        familyCode: code || "",
        centerSessionId: "",
        termId: "",
        firstName: "",
        greGorianDOB: "",
        gender: "",
        homeLanguageId: "",
        centerClassId: "",
        classSectionId: "",
        nationality: "",
        email: "",
        createdBy: localStorage.getItem("userId"),
        updatedBy: localStorage.getItem("userId"),
        userId: userId,
        organizationId: organizationId,
        centerId: centerId,
        code: "",
        isSubmit: false,
        isCenterSubmit: false,
        centerUpdatedBy: "",
        assessmentSlotId: 0,
        centerConcessionId: "",
      },
    },
  });

  const [termsData, setTermsData] = useState([]);
  const [sessionsData, setSessionsData] = useState([]);
  const [classesData, setClassesData] = useState([]);
  const [classSectionsData, setClassSectionsData] = useState([]);
  const [homeLanguageData, setHomeLanguageData] = useState([]);
  const [nationalityData, setNationalityData] = useState([]);
  const [concessionData, setConcessionData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isSettingInitialData, setIsSettingInitialData] = useState(false);
  const [customDropdownName, setCustomDropdownName] = useState("");
  const [
    isCustomDropdownValuesModalVisible,
    setIsCustomDropdownValuesModalVisible,
  ] = useState(false);

  // Fetch student personal profile
  const fetchStudentPersonalProfile = async () => {
    if (!token || !selectedStudent.studentFirstRegistrationId) {
      console.error("Unauthorized or no student ID.");
      return;
    }

    try {
      const response = await get(
        `InspStudentFirstRegistrtion/GetStudentPersonalProfileInfo/${selectedStudent.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess && response.studentPersonalInfoDto) {
        const studentData = response.studentPersonalInfoDto;
        console.log("Student Data:", studentData);

        setIsSettingInitialData(true);

        // Set basic fields
        const updateData = {
          firstName: studentData.studentFullName || "",
          greGorianDOB: studentData.dob?.split("T")[0] || "",
          termId: studentData.entryTermId?.toString() || "",
          nationality: studentData.nationality || "",
          email: studentData.email || "",
          code: studentData.code || "",
          homeLanguageId: studentData.languageId,
          gender: studentData.genderId?.toString() || "",
          centerSessionId: studentData.applyingForSession || "",
          centerConcessionId: studentData.centerConcessionId?.toString() || "",
        };

        // Set values individually
        Object.keys(updateData).forEach((key) => {
          setValue(`currentPupil.${key}`, updateData[key]);
        });

        // Handle centerSessionId - don't override if already set
        const currentSessionId = getValues("currentPupil.centerSessionId");
        if (!currentSessionId && studentData.centerSessionId) {
          setValue(
            "currentPupil.centerSessionId",
            studentData.centerSessionId.toString()
          );
        }

        // Handle centerClassId and classSectionId
        if (studentData.classId) {
          console.log("Setting centerClassId:", studentData.classId.toString());
          setValue(
            "currentPupil.centerClassId",
            studentData.classId.toString()
          );

          // Fetch class sections
          await fetchClassSectionByCenterClassIdAnd(studentData.classId);

          // Set classSectionId after sections are loaded
          setTimeout(() => {
            if (studentData.classSectionId) {
              console.log(
                "Setting classSectionId:",
                studentData.classSectionId.toString()
              );
              setValue(
                "currentPupil.classSectionId",
                studentData.classSectionId.toString()
              );
            }
            setIsSettingInitialData(false);
          }, 500);
        } else {
          setIsSettingInitialData(false);
        }
      }
    } catch (error) {
      console.error("Error fetching student personal profile:", error);
      setIsSettingInitialData(false);
    }
  };

  // Fetch terms
  const fetchTermsByOrgId = async () => {
    try {
      const response = await get(
        `StudentEvaluation/GetAllEvaluationCriteriaType?OrganizationId=${organizationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setTermsData(response?.evaluationTypes || []);
    } catch (error) {
      console.error("Error fetching terms:", error);
      setTermsData([]);
    }
  };

  // Fetch sessions
  const getSessionsList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `Session/GetSessionByCenterId?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const sessions = response.session || [];
      setSessionsData(sessions);

      // Set default session only if not already set
      const currentSessionId = getValues("currentPupil.centerSessionId");
      if (sessions.length > 0 && !currentSessionId) {
        setValue("currentPupil.centerSessionId", sessions[0].id.toString());
      }
    } catch (error) {
      console.error("Error fetching sessions:", error);
      setSessionsData([]);
    }
  };

  // Fetch classes by center ID
  const fetchClassesByCenterId = async () => {
    try {
      const response = await get(
        `CenterClass/GetClassCenterByCenterId?id=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setClassesData(response || []);
    } catch (error) {
      console.error("Error fetching classes:", error);
      setClassesData([]);
    }
  };

  // Fetch class sections by center class ID
  const fetchClassSectionByCenterClassIdAnd = async (centerClassId) => {
    try {
      if (centerClassId && centerId) {
        console.log("Fetching sections for centerClassId:", centerClassId);
        const response = await get(
          `ClassSection/GetAllSectionByCenterClassIdAndCenterId?centerClassId=${centerClassId}&centerId=${centerId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const sections = response || [];
        console.log("Class sections response:", sections);
        setClassSectionsData(sections);
        return sections;
      } else {
        setClassSectionsData([]);
        return [];
      }
    } catch (error) {
      console.error("Error fetching class sections:", error);
      setClassSectionsData([]);
      return [];
    }
  };

  // Fetch common settings
  const fetchCommonSettings = async (moduleName, setData) => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=${moduleName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setData(response?.commonSettingDetail || []);
    } catch (error) {
    }
  };

  // Fetch concession data
  const fetchConcession = async () => {
    try {
      const response = await get(
        `ConcessionType/GetCenterConcessionByCenterId?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const groupedOptions = response.concession.map((group) => ({
        label: group.label,
        options: group.concessionDiscounts.map((concession) => ({
          value: concession.concessionTypeId,
          label: concession.concessionTypeName,
        })),
      }));
      setConcessionData(groupedOptions);
    } catch (err) {
      console.error("Error fetching concession:", err);
    }
  };

  // Initial data loading
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);

      // Load all basic data first
      await Promise.all([
        fetchTermsByOrgId(),
        getSessionsList(),
        fetchClassesByCenterId(),
        fetchCommonSettings("homeLanguage", setHomeLanguageData),
        fetchCommonSettings("nationality", setNationalityData),
        fetchConcession(),
      ]);

      // Then load student profile
      await fetchStudentPersonalProfile();

      setIsLoading(false);
      setIsDataLoaded(true);
    };

    if (selectedStudent.studentFirstRegistrationId && centerId) {
      loadInitialData();
    }
  }, [selectedStudent.studentFirstRegistrationId, centerId]);

  // Watch centerClassId to fetch class sections when it changes
  useEffect(() => {
    const subscription = watch(async (value, { name }) => {
      if (
        name === "currentPupil.centerClassId" &&
        value.currentPupil?.centerClassId &&
        !isSettingInitialData
      ) {
        console.log("Class changed to:", value.currentPupil.centerClassId);
        await fetchClassSectionByCenterClassIdAnd(
          value.currentPupil.centerClassId
        );

        // Only reset classSectionId if we're not in initial data loading
        if (isDataLoaded && !isSettingInitialData) {
          console.log("Resetting classSectionId due to class change");
          setValue("currentPupil.classSectionId", "");
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue, isDataLoaded, isSettingInitialData]);

  // Pupils Field Array
  const {
    fields: admissionInquiryDtos,
    append: appendPupil,
    remove: removePupil,
    update: updatePupil,
  } = useFieldArray({
    control,
    name: "admissionInquiryDtos",
    keyName: "rhfId",
  });

  // Fetch pupils list
  const getPupilsList = async () => {
    if (!token || !userId) {
      console.error("Unauthorized or no valid userId available");
      return;
    }

    setIsLoading(true);

    try {
      const response = await get(
        `Student/GetPupilsInformationByUserId?userId=${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response && response.length > 0) {
        const mappedPupils = await Promise.all(
          response.map(async (pupil) => {
            // Fetch class sections for each pupil if they have a centerClassId
            if (pupil.centerClassId) {
              await fetchClassSectionByCenterClassIdAnd(pupil.centerClassId);
            }

            return {
              pupilId: pupil.id,
              rhfId: pupil.id,
              familyCode: pupil.familyCode || code || "",
              centerSessionId: pupil.applyingForSession || "",
              termId: pupil.termId || "",
              firstName: pupil.firstName || "",
              greGorianDOB: pupil.greGorianDOB?.split("T")[0] || "",
              gender: pupil.gender,
              homeLanguageId: pupil.homeLanguageId || "",
              centerClassId: pupil.centerClassId,
              classSectionId: pupil.classSectionId,
              nationality: pupil.nationality || "",
              email: pupil.email || "",
              createdBy: pupil.createdBy || localStorage.getItem("userId"),
              updatedBy: pupil.updatedBy || localStorage.getItem("userId"),
              userId: userId,
              organizationId: organizationId,
              centerId: centerId,
              code: pupil.code || "",
              isSubmit: pupil.isSubmit,
              isCenterSubmit: pupil.isCenterSubmit,
              centerSubmissionDate: pupil.centerSubmissionDate,
              centerUpdatedBy: pupil.centerUpdatedBy,
              assessmentSlotId: pupil.assessmentSlotId,
              assessmentDate: pupil.assessmentDate,
              communicationRemarks: pupil.communicationRemarks || "",
              isCommunicatedToGuardian: pupil.isCommunicatedToGuardian,
              isInterviewScheduled: pupil.isInterviewScheduled,
              isInterviewTaken: pupil.isInterviewTaken,
              interviewRemarks: pupil.interviewRemarks || "",
              interviewDate: pupil.interviewDate,
              isAssessmentAdded: pupil.isAssessmentAdded,
              isAssessmentMarksAdded: pupil.isAssessmentMarksAdded,
              isAccepted: pupil.isAccepted,
              acceptanceRemarks: pupil.acceptanceRemarks || "",
              hasObservation: pupil.hasObservation,
              observationRemarks: pupil.observationRemarks || "",
              shouldRetakeExam: pupil.shouldRetakeExam,
              retakeExamRemarks: pupil.retakeExamRemarks || "",
              registrationVoucherNo: pupil.registrationVoucherNo,
              isPaidRegistration: pupil.isPaidRegistration,
              admissionVoucherNo: pupil.admissionVoucherNo,
              isPaidAdmission: pupil.isPaidAdmission,
            };
          })
        );

        setValue("admissionInquiryDtos", mappedPupils);
        const pupilPhoto = {
          name: mappedPupils.fileName,
          type: mappedPupils.fileName.split(".").pop().toLowerCase(),
          size: 0,
          base64: "",
          filePath: mappedPupils.filePath,
          documentType: "pupilPhoto",
          id: mappedPupils.id,
          tagPriority: mappedPupils.tagPriority,
          tagName: mappedPupils.tagName,
          tagComments: mappedPupils.tagComments,
          documentTagId: mappedPupils.documentTagId,
        };
        setProfilePictureState(pupilPhoto);
        setProfilePicture(
          mappedPupils.imagePath
            ? `${process.env.REACT_APP_LIVE_DOMAIN_URL.replace(
                /\/+$/,
                ""
              )}/ExternalImages${mappedPupils.imagePath}`
            : null
        );
      }
    } catch (error) {
      console.error("Error fetching pupils:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load pupils when userId changes
  useEffect(() => {
    if (userId && isDataLoaded) {
      getPupilsList();
    }
  }, [userId, isDataLoaded]);

  // Update current pupil when admissionInquiryDtos changes
  useEffect(() => {
    if (admissionInquiryDtos.length > 0 && selectedStudent && isDataLoaded) {
      const matchedPupil = admissionInquiryDtos.find(
        (pupil) => pupil.pupilId === selectedStudent.studentFirstRegistrationId
      );

      if (matchedPupil) {
        console.log("Matched pupil:", matchedPupil);

        setIsSettingInitialData(true);

        // Set all values including classSectionId
        Object.keys(matchedPupil).forEach((key) => {
          if (key !== "rhfId" && matchedPupil[key] !== undefined) {
            setValue(`currentPupil.${key}`, matchedPupil[key]);
          }
        });

        // Fetch class sections for the matched pupil if needed
        if (matchedPupil.centerClassId) {
          fetchClassSectionByCenterClassIdAnd(matchedPupil.centerClassId);
        }

        setTimeout(() => {
          setIsSettingInitialData(false);
        }, 500);
      }
    }
  }, [admissionInquiryDtos, selectedStudent, setValue, isDataLoaded]);

  // Debug: Log current form values and class sections
  useEffect(() => {
    if (isDataLoaded) {
      const currentValues = getValues();
      console.log("Current form values:", currentValues.currentPupil);
      console.log("Available class sections:", classSectionsData);
    }
  }, [getValues, classSectionsData, isDataLoaded]);

  // NEW: Save student profile API call
  const saveStudentProfile = async (payload) => {
    if (!token) {
      console.error("Unauthorized.");
      return false;
    }

    setIsLoading(true);

    try {
      const response = await post(
        "InspStudentFirstRegistrtion/UpdateStudentProfileInfo",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.isSuccess) {
        toast.success(
          "Student profile updated successfully!" || response.message
        );
        return true;
      } else {
        toast.error(response.message || "An unexpected error occurred");
        return false;
      }
    } catch (error) {
      console.error("Error saving student profile:", error);
      toast.error("Failed to save student profile");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // UPDATED: Save function for single student profile
  const save = async () => {
    let isValid = true;
    const currentPupil = getValues("currentPupil");

    console.log("Saving student profile data:", currentPupil);

    // Validate required fields
    const requiredFields = [
      "firstName",
      "greGorianDOB",
      "gender",
      "nationality",
      "email",
      "centerClassId",
      "classSectionId",
      "centerSessionId",
      "termId",
    ];

    const missingFields = requiredFields.filter(
      (field) =>
        !currentPupil[field] || String(currentPupil[field]).trim() === ""
    );

    if (missingFields.length > 0) {
      toast.error(
        `Please fill all required fields: ${missingFields.join(", ")}`
      );
      return false;
    }

    // Trigger form validation
    isValid = await trigger("currentPupil");
    if (!isValid) {
      toast.error("Please fill all required fields correctly");
      return false;
    }

    // Prepare payload according to the new API structure
    const payload = {
      firstRegistrationId: selectedStudent.studentFirstRegistrationId || 0,
      studentFullName: currentPupil.firstName || "",
      applyingForSession: parseInt(currentPupil.centerSessionId) || 0,
      classId: parseInt(currentPupil.centerClassId) || 0,
      dob: currentPupil.greGorianDOB
        ? `${currentPupil.greGorianDOB}T00:00:00.000Z`
        : null,
      entryTermId: parseInt(currentPupil.termId) || 0,
      admissionDate: new Date().toISOString(), // Current date as admission date
      rollNo: 0, // Default value
      code: currentPupil.code || "",
      studentImage: "", // Empty string as default
      imagePath: "", // Empty string as default
      centerSessionId: parseInt(currentPupil.centerSessionId) || 0,
      countryId: 0, // Default value
      stateId: 0, // Default value
      cityId: 0, // Default value
      placeOfBirth: "", // Empty string as default
      nationality: currentPupil.nationality || "",
      email: currentPupil.email || "",
      classSectionId: parseInt(currentPupil.classSectionId) || 0,
      genderId: parseInt(currentPupil.gender) || 0,
      languageId: parseInt(currentPupil.homeLanguageId) || 0,
      centerConcessionId: parseInt(currentPupil.centerConcessionId) || 0,
    };

    console.log("Sending payload to API:", payload);

    return await saveStudentProfile(payload);
  };

  const handleCustomDropdownSetting = (dropdownName) => {
    setCustomDropdownName(dropdownName);
    setIsCustomDropdownValuesModalVisible(true);
  };

  return (
    <div className="profile-content-area d-flex flex-column h-100">
      <div className="mb-5">
        <h5 className="mb-0 fw-bold">Student Information</h5>
      </div>

      <div className="profile-scrollable-area p-1 flex-grow-1">
        <h5 className="mb-3 p-2 px-3 text-secondary bg-brand-light rounded-pill">
          Details
        </h5>

        <div className="card shadow-sm">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Student Full Name <span className="text-danger fs-9">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control custom-input ${
                    errors.currentPupil?.firstName ? "is-invalid" : ""
                  }`}
                  {...register("currentPupil.firstName", {
                    required: "Required",
                  })}
                  placeholder="Enter Student Full Name"
                />
                {errors.currentPupil?.firstName && (
                  <div className="invalid-feedback">
                    {errors.currentPupil.firstName.message}
                  </div>
                )}
              </div>

              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  DOB <span className="text-danger fs-9">*</span>
                </label>
                <input
                  type="date"
                  className={`form-control custom-input ${
                    errors.currentPupil?.greGorianDOB ? "is-invalid" : ""
                  }`}
                  {...register("currentPupil.greGorianDOB", {
                    required: "Required",
                    validate: {
                      notFutureDate: (value) =>
                        (value && new Date(value) <= new Date()) ||
                        "Future dates are not allowed.",
                      notBefore2000: (value) =>
                        (value && new Date(value) >= new Date("2000-01-01")) ||
                        "Dates before the year 2000 are not allowed.",
                    },
                  })}
                  min="2000-01-01"
                  max={new Date().toISOString().split("T")[0]}
                />
                {errors.currentPupil?.greGorianDOB && (
                  <div className="invalid-feedback">
                    {errors.currentPupil.greGorianDOB.message}
                  </div>
                )}
              </div>

              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Gender <span className="text-danger fs-9">*</span>
                </label>
                <select
                  className={`form-select custom-input ${
                    errors.currentPupil?.gender ? "is-invalid" : ""
                  }`}
                  {...register("currentPupil.gender", { required: "Required" })}
                >
                  <option value="">Select Gender</option>
                  <option value="1">Male</option>
                  <option value="2">Female</option>
                </select>
                {errors.currentPupil?.gender && (
                  <div className="invalid-feedback">
                    {errors.currentPupil.gender.message}
                  </div>
                )}
              </div>

              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">Language</label>
                <select
                  className="form-select custom-input"
                  {...register("currentPupil.homeLanguageId")}
                >
                  <option value="">Select Language</option>
                  {homeLanguageData.map((lang) => (
                    <option key={lang.id} value={lang.id}>
                      {lang.value}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Nationality <span className="text-danger fs-9">*</span>
                </label>
                <select
                  className={`form-select custom-input ${
                    errors.currentPupil?.nationality ? "is-invalid" : ""
                  }`}
                  {...register("currentPupil.nationality", {
                    required: "Required",
                  })}
                >
                  <option value="">Select Nationality</option>
                  {nationalityData.map((nat) => (
                    <option key={nat.id} value={nat.id}>
                      {nat.value}
                    </option>
                  ))}
                </select>
                {errors.currentPupil?.nationality && (
                  <div className="invalid-feedback">
                    {errors.currentPupil.nationality.message}
                  </div>
                )}
              </div>

              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Email <span className="text-danger fs-9">*</span>
                </label>
                <input
                  type="email"
                  className={`form-control custom-input ${
                    errors.currentPupil?.email ? "is-invalid" : ""
                  }`}
                  {...register("currentPupil.email", {
                    required: "Required",
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: "Invalid email address",
                    },
                  })}
                  placeholder="Enter Email"
                />
                {errors.currentPupil?.email && (
                  <div className="invalid-feedback">
                    {errors.currentPupil.email.message}
                  </div>
                )}
              </div>

              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Class <span className="text-danger fs-9">*</span>
                </label>
                <select
                  className={`form-select custom-input ${
                    errors.currentPupil?.centerClassId ? "is-invalid" : ""
                  }`}
                  {...register("currentPupil.centerClassId", {
                    required: "Required",
                  })}
                >
                  <option value="">Select Year Group</option>
                  {classesData.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {errors.currentPupil?.centerClassId && (
                  <div className="invalid-feedback">
                    {errors.currentPupil.centerClassId.message}
                  </div>
                )}
              </div>

              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Section <span className="text-danger fs-9">*</span>
                </label>
                <select
                  className={`form-select custom-input ${
                    errors.currentPupil?.classSectionId ? "is-invalid" : ""
                  }`}
                  {...register("currentPupil.classSectionId", {
                    required: "Required",
                  })}
                >
                  <option value="">Select Section</option>
                  {classSectionsData.map((section) => (
                    <option key={section.id} value={section.id}>
                      {section.name}
                    </option>
                  ))}
                </select>
                {errors.currentPupil?.classSectionId && (
                  <div className="invalid-feedback">
                    {errors.currentPupil.classSectionId.message}
                  </div>
                )}
              </div>

              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Applying For <span className="text-danger fs-9">*</span>
                </label>
                <select
                  className={`form-select custom-input ${
                    errors.currentPupil?.centerSessionId ? "is-invalid" : ""
                  }`}
                  {...register("currentPupil.centerSessionId", {
                    required: "Required",
                  })}
                >
                  <option value="">Select Applying For</option>
                  {sessionsData.map((session) => (
                    <option key={session.id} value={session.id}>
                      {session.name}
                    </option>
                  ))}
                </select>
                {errors.currentPupil?.centerSessionId && (
                  <div className="invalid-feedback">
                    {errors.currentPupil.centerSessionId.message}
                  </div>
                )}
              </div>

              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Entry Term <span className="text-danger fs-9">*</span>
                </label>
                <select
                  className={`form-select custom-input ${
                    errors.currentPupil?.termId ? "is-invalid" : ""
                  }`}
                  {...register("currentPupil.termId", { required: "Required" })}
                >
                  <option value="">Select Entry Term</option>
                  {termsData.map((term) => (
                    <option key={term.id} value={term.id}>
                      {term.type}
                    </option>
                  ))}
                </select>
                {errors.currentPupil?.termId && (
                  <div className="invalid-feedback">
                    {errors.currentPupil.termId.message}
                  </div>
                )}
              </div>

              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Concession
                </label>
                <select
                  className="form-select custom-input"
                  {...register("currentPupil.centerConcessionId")}
                >
                  <option value="">Select Concession</option>
                  {concessionData.map((group, groupIdx) => (
                    <optgroup key={groupIdx} label={group.label}>
                      {group.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

            </div>
          </div>
        </div>
      </div>

      <div className="mt-auto pt-3">
        <div className="d-flex justify-content-end gap-3">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={async () => {
              await save();
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <span
                className="spinner-border spinner-custom-size me-1"
                aria-hidden="true"
              ></span>
            ) : null}
            Save
          </button>
        </div>
      </div>

      {isCustomDropdownValuesModalVisible &&
        createPortal(
          <DropdownWithCustomValuesModal
            dropdownName={customDropdownName}
            onClose={() => {
              setIsCustomDropdownValuesModalVisible(false);
              if (customDropdownName === "homeLanguage") {
                fetchCommonSettings("homeLanguage", setHomeLanguageData);
              } else if (customDropdownName === "nationality") {
                fetchCommonSettings("nationality", setNationalityData);
              }
            }}
          />,
          document.body
        )}
    </div>
  );
};

export default PersonalInfoForm;
